import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { UserPlus, Trash2, ShieldCheck, Plus, Save } from "lucide-react";
import { useUiPrefs } from "@/lib/uiPrefs";
import { DensityToggle, tableDensity } from "@/components/ops-ui";

const emptyUser = { email: "", name: "", password: "", role: "Warehouse Team" };

export default function Users() {
  const { can } = useAuth();
  const [tab, setTab] = useState("users");
  const [users, setUsers] = useState([]);
  const [roles, setRoles] = useState([]);
  const [catalog, setCatalog] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(emptyUser);
  const [selRole, setSelRole] = useState(null);
  const [draft, setDraft] = useState(new Set());
  const [newRoleOpen, setNewRoleOpen] = useState(false);
  const [newRoleName, setNewRoleName] = useState("");
  const { density, setDensity } = useUiPrefs("users");

  const loadUsers = () => api.get("/users").then(r => setUsers(r.data)).catch(() => {});
  const loadRoles = () => api.get("/roles").then(r => setRoles(r.data)).catch(() => {});
  useEffect(() => {
    if (can("user:view")) loadUsers();
    if (can("role:view")) { loadRoles(); api.get("/permissions/catalog").then(r => setCatalog(r.data.modules)).catch(() => {}); }
    // eslint-disable-next-line
  }, []);

  // ---- Users ----
  const createUser = async () => {
    try { await api.post("/users", form); toast.success("User created"); setOpen(false); setForm(emptyUser); loadUsers(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const delUser = async (id) => {
    if (!window.confirm("Delete user?")) return;
    try { await api.delete(`/users/${id}`); toast.success("Deleted"); loadUsers(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const assignRole = async (id, role) => {
    try { await api.put(`/users/${id}`, { role }); toast.success("Role updated"); loadUsers(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  // ---- Roles ----
  const pickRole = (role) => { setSelRole(role); setDraft(new Set(role.permissions || [])); };
  const toggle = (key) => setDraft(prev => { const n = new Set(prev); n.has(key) ? n.delete(key) : n.add(key); return n; });
  const selectModule = (mod, on) => setDraft(prev => {
    const n = new Set(prev); mod.permissions.forEach(p => on ? n.add(p.key) : n.delete(p.key)); return n;
  });
  const saveRole = async () => {
    try {
      await api.put(`/roles/${encodeURIComponent(selRole.name)}`, { name: selRole.name, permissions: [...draft] });
      toast.success("Permissions saved"); loadRoles();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const createRole = async () => {
    if (!newRoleName.trim()) return toast.error("Role name required");
    try {
      const { data } = await api.post("/roles", { name: newRoleName.trim(), permissions: [] });
      toast.success("Role created"); setNewRoleOpen(false); setNewRoleName("");
      await loadRoles(); pickRole(data);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const delRole = async (name) => {
    if (!window.confirm(`Delete role "${name}"?`)) return;
    try { await api.delete(`/roles/${encodeURIComponent(name)}`); toast.success("Role deleted"); setSelRole(null); loadRoles(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <div>
      <header className="mb-6">
        <div className="caption">Access management</div>
        <h1 className="font-heading text-3xl font-extrabold mt-1">Users &amp; Roles</h1>
        <p className="text-slate-500 mt-1 text-sm">Create roles, tick permissions per module, and assign roles to staff.</p>
      </header>

      <div className="flex gap-1 mb-5 border-b border-slate-200">
        {can("user:view") && (
          <button data-testid="tab-users" onClick={() => setTab("users")}
            className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px ${tab === "users" ? "border-slate-900 text-slate-900" : "border-transparent text-slate-500"}`}>Users</button>
        )}
        {can("role:view") && (
          <button data-testid="tab-roles" onClick={() => setTab("roles")}
            className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px ${tab === "roles" ? "border-slate-900 text-slate-900" : "border-transparent text-slate-500"}`}>Roles &amp; Permissions</button>
        )}
      </div>

      {tab === "users" && can("user:view") && (
        <div>
          <div className="flex items-center justify-end gap-2 mb-3">
            <DensityToggle density={density} setDensity={setDensity} />
            {can("user:create") && (
              <Dialog open={open} onOpenChange={setOpen}>
                <DialogTrigger asChild>
                  <Button data-testid="user-new-button" className="bg-sky-600 hover:bg-sky-700"><UserPlus className="w-4 h-4 mr-1.5" />Add user</Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader><DialogTitle>New user</DialogTitle></DialogHeader>
                  <div className="space-y-3">
                    <div><Label>Name *</Label><Input data-testid="user-name-input" value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} /></div>
                    <div><Label>Email *</Label><Input data-testid="user-email-input" type="email" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} /></div>
                    <div><Label>Password *</Label><Input data-testid="user-password-input" type="password" value={form.password} onChange={e => setForm({ ...form, password: e.target.value })} /></div>
                    <div><Label>Role</Label>
                      <Select value={form.role} onValueChange={v => setForm({ ...form, role: v })}>
                        <SelectTrigger data-testid="user-role-select"><SelectValue /></SelectTrigger>
                        <SelectContent>{roles.map(r => <SelectItem key={r.name} value={r.name}>{r.name}</SelectItem>)}</SelectContent>
                      </Select>
                    </div>
                  </div>
                  <DialogFooter>
                    <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
                    <Button onClick={createUser} data-testid="user-save-button" className="bg-slate-900">Create</Button>
                  </DialogFooter>
                </DialogContent>
              </Dialog>
            )}
          </div>
          <div className="rounded-lg border border-slate-200 bg-white overflow-x-auto">
            <table className={`excel-table w-full ${tableDensity(density)}`} data-testid="users-table">
              <thead><tr><th>Name</th><th>Email</th><th>Role</th><th>Created</th><th></th></tr></thead>
              <tbody>
                {users.map(u => (
                  <tr key={u.id} data-testid={`user-row-${u.email}`}>
                    <td>{u.name}</td>
                    <td className="font-mono text-xs">{u.email}</td>
                    <td>
                      {can("role:assign") ? (
                        <Select value={u.role} onValueChange={v => assignRole(u.id, v)}>
                          <SelectTrigger className="h-8 w-44" data-testid={`user-role-${u.email}`}><SelectValue /></SelectTrigger>
                          <SelectContent>{roles.map(r => <SelectItem key={r.name} value={r.name}>{r.name}</SelectItem>)}</SelectContent>
                        </Select>
                      ) : <span className="px-2 py-0.5 rounded text-xs font-medium bg-slate-100 text-slate-700">{u.role}</span>}
                    </td>
                    <td className="text-slate-500 text-xs">{u.created_at?.slice(0, 10)}</td>
                    <td className="text-right">
                      {can("user:delete") && <Button variant="ghost" size="sm" onClick={() => delUser(u.id)} data-testid={`user-del-${u.email}`}><Trash2 className="w-4 h-4 text-rose-600" /></Button>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {tab === "roles" && can("role:view") && (
        <div className="grid grid-cols-[240px_1fr] gap-6">
          {/* Roles list */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <span className="caption">Roles</span>
              {can("role:create") && (
                <Dialog open={newRoleOpen} onOpenChange={setNewRoleOpen}>
                  <DialogTrigger asChild><Button size="sm" variant="outline" data-testid="role-new-button"><Plus className="w-3.5 h-3.5 mr-1" />New</Button></DialogTrigger>
                  <DialogContent>
                    <DialogHeader><DialogTitle>Create role</DialogTitle></DialogHeader>
                    <div><Label>Role name</Label><Input value={newRoleName} onChange={e => setNewRoleName(e.target.value)} data-testid="role-name-input" placeholder="e.g. Accountant" autoFocus /></div>
                    <DialogFooter>
                      <Button variant="outline" onClick={() => setNewRoleOpen(false)}>Cancel</Button>
                      <Button onClick={createRole} data-testid="role-create-confirm" className="bg-slate-900">Create</Button>
                    </DialogFooter>
                  </DialogContent>
                </Dialog>
              )}
            </div>
            <div className="space-y-1">
              {roles.map(r => (
                <button key={r.name} onClick={() => pickRole(r)} data-testid={`role-item-${r.name}`}
                  className={`w-full text-left px-3 py-2 rounded-md text-sm flex items-center justify-between ${selRole?.name === r.name ? "bg-sky-50 border border-sky-300 text-sky-800" : "hover:bg-slate-100 border border-transparent"}`}>
                  <span className="flex items-center gap-1.5">{r.all_access && <ShieldCheck className="w-3.5 h-3.5 text-emerald-600" />}{r.name}</span>
                  <span className="text-[10px] text-slate-400">{r.all_access ? "ALL" : (r.permissions?.length || 0)}</span>
                </button>
              ))}
            </div>
          </div>

          {/* Permission grid */}
          <div>
            {!selRole && <div className="text-slate-400 text-sm border border-dashed rounded p-10 text-center">Select a role to view &amp; edit its permissions.</div>}
            {selRole && selRole.all_access && (
              <div className="border border-emerald-200 bg-emerald-50/40 rounded p-6 text-sm text-emerald-800" data-testid="role-all-access">
                <ShieldCheck className="w-5 h-5 mb-2" /><b>{selRole.name}</b> has <b>full access (all permissions)</b> and cannot be restricted.
              </div>
            )}
            {selRole && !selRole.all_access && (
              <div>
                <div className="flex items-center justify-between mb-3 sticky top-0 bg-white py-2 z-10">
                  <div><span className="font-semibold">{selRole.name}</span> <span className="text-xs text-slate-400 ml-1">{draft.size} selected</span></div>
                  <div className="flex gap-2">
                    {!selRole.system && can("role:delete") && <Button size="sm" variant="ghost" onClick={() => delRole(selRole.name)} className="text-rose-600" data-testid="role-delete-button"><Trash2 className="w-4 h-4 mr-1" />Delete</Button>}
                    {can("role:edit") && <Button size="sm" onClick={saveRole} data-testid="role-save-button" className="bg-sky-600 hover:bg-sky-700"><Save className="w-4 h-4 mr-1.5" />Save Permissions</Button>}
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  {catalog.map(mod => {
                    const allOn = mod.permissions.every(p => draft.has(p.key));
                    return (
                      <div key={mod.key} className="border border-slate-200 rounded-lg p-3" data-testid={`perm-module-${mod.key}`}>
                        <div className="flex items-center justify-between mb-2 pb-2 border-b border-slate-100">
                          <span className="font-semibold text-sm">{mod.module}</span>
                          <div className="flex gap-1 text-[11px]">
                            <button onClick={() => selectModule(mod, true)} data-testid={`perm-all-${mod.key}`} className="px-1.5 py-0.5 rounded bg-slate-100 hover:bg-slate-200">Select All</button>
                            <button onClick={() => selectModule(mod, false)} data-testid={`perm-clear-${mod.key}`} className="px-1.5 py-0.5 rounded bg-slate-100 hover:bg-slate-200">Clear</button>
                          </div>
                        </div>
                        <div className="space-y-1.5">
                          {mod.permissions.map(p => (
                            <label key={p.key} className="flex items-start gap-2 text-sm cursor-pointer">
                              <Checkbox checked={draft.has(p.key)} onCheckedChange={() => toggle(p.key)} data-testid={`perm-${p.key}`} className="mt-0.5" />
                              <span className={draft.has(p.key) ? "text-slate-800" : "text-slate-500"}>{p.label}</span>
                            </label>
                          ))}
                        </div>
                        {allOn && <div className="text-[10px] text-emerald-600 mt-1.5">All selected</div>}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
